import { forwardRef, useEffect, useRef, useState } from 'react';
import {
  Select as SelectRoot,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from './ui/select';

interface Props {
  items: {
    value: string | number;
    label: string;
    disabled?: boolean;
    comment?: string;
  }[];
  defaultLabel?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  visibleItems?: number; // 表示するアイテム数（デフォルト: 7）
  id?: string;
  footer?: React.ReactNode;
  // compact は選択系コンポーネント統合の互換のため (#3277)
  variant?: 'default' | 'compact';
  onOpenChange?: (open: boolean) => void;
}

const Select = forwardRef<React.ElementRef<typeof SelectTrigger>, Props>(function Select(
  {
    items,
    className,
    defaultLabel,
    value,
    onValueChange,
    disabled,
    visibleItems = 7,
    id,
    footer,
    variant = 'default',
    onOpenChange,
    ...others
  },
  ref
) {
  // アイテムの高さを計算（p-2 + text-sm ≈ 40px）
  const itemHeight = 40;
  const maxHeight = `${visibleItems * itemHeight}px`;

  const [hoveredValue, setHoveredValue] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    if (hoveredValue && itemRefs.current[hoveredValue]) {
      const rect = itemRefs.current[hoveredValue]!.getBoundingClientRect();
      setTooltipPos({ top: rect.top + rect.height / 2, left: rect.right + 8 });
    } else {
      setTooltipPos(null);
    }
  }, [hoveredValue]);

  const hoveredItem = items.find((item) => String(item.value) === hoveredValue);

  const handleValueChange = (nextValue: string) => {
    setHoveredValue(null);
    onValueChange?.(nextValue);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setHoveredValue(null);
    }
    onOpenChange?.(open);
  };

  return (
    <>
      <SelectRoot
        value={value ?? ''}
        onValueChange={handleValueChange}
        onOpenChange={handleOpenChange}
        disabled={disabled}
        {...others}
      >
        <SelectTrigger ref={ref} className={className} id={id} variant={variant}>
          <SelectValue placeholder={defaultLabel} />
        </SelectTrigger>
        <SelectContent maxHeight={maxHeight} footer={footer} variant={variant}>
          {items.map((item) => (
            <SelectItem
              key={item.value}
              value={String(item.value)}
              disabled={item.disabled}
              variant={variant}
              ref={(el) => {
                itemRefs.current[String(item.value)] = el;
              }}
              onMouseEnter={() => item.comment && setHoveredValue(String(item.value))}
              onMouseLeave={() => item.comment && setHoveredValue(null)}
            >
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </SelectRoot>

      {tooltipPos && hoveredItem?.comment && (
        <div
          className="pointer-events-none fixed z-[10000] flex max-w-[330px] items-center bg-black p-2 text-xs leading-[170%] break-words text-white shadow-md"
          style={{
            top: `${tooltipPos.top}px`,
            left: `${tooltipPos.left + 3}px`,
            transform: 'translateY(-50%)',
          }}
        >
          <div
            className="absolute top-1/2 left-[-6px] -translate-y-1/2"
            style={{
              width: '0',
              height: '0',
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: '8px solid black',
            }}
          />
          {hoveredItem.comment}
        </div>
      )}
    </>
  );
});

export default Select;
